import { Add, Delete, FilterList, SwapHoriz } from '@mui/icons-material';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { TooltipIconButton } from 'components/common/icon/tooltip-icon-button';
import { PurpleToggle } from 'components/common/toggle/purple-toggle';
import { FC, useEffect, useRef, useState } from 'react';
import { LeadFieldKey } from 'types/apollo/globalTypes';

export interface MetadataFilter {
  key?: string;
  fieldKey?: LeadFieldKey;
  value?: string;
  and?: MetadataFilter[];
  or?: MetadataFilter[];
}

const FIELD_KEY_OPTIONS: { value: LeadFieldKey; label: string }[] = [
  { value: LeadFieldKey.REGION, label: 'region' },
  { value: LeadFieldKey.SOURCE, label: 'source' },
];

const getFieldKeyLabel = (fieldKey: LeadFieldKey): string => {
  return FIELD_KEY_OPTIONS.find((f) => f.value === fieldKey)?.label || fieldKey.toLowerCase();
};

const parseFieldKeyFromLabel = (label: string): LeadFieldKey | null => {
  const found = FIELD_KEY_OPTIONS.find((f) => f.label.toLowerCase() === label.toLowerCase());
  return found?.value || null;
};

type OperatorType = 'AND' | 'OR';
type FilterMode = 'visual' | 'text';

// Syntax highlighting for query text
const highlightQuerySyntax = (text: string) => {
  const parts: { text: string; color: string }[] = [];
  let current = '';
  let inQuotes = false;
  let parenDepth = 0;

  const colors = {
    key: '#2563eb', // blue
    value: '#059669', // green
    quotedValue: '#059669', // green
    operator: '#ea8f33', // orange
    paren: '#6926dc', // purple
    colon: '#64748b', // gray
    equals: '#64748b', // gray (same as colon)
  };

  const getParenColor = (depth: number) => {
    const parenColors = ['#9333ea', '#ec4899', '#f59e0b', '#06b6d4'];
    return parenColors[depth % parenColors.length];
  };

  // Helper to check if the next non-space character is a colon or equals
  const isKeyQuote = (fromIndex: number): boolean => {
    for (let j = fromIndex; j < text.length; j++) {
      const c = text[j];
      if (c === ':' || c === '=') return true;
      if (c !== ' ') return false;
    }
    return false;
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const prevChar = i > 0 ? text[i - 1] : '';

    if (char === '"' && prevChar !== '\\') {
      if (inQuotes) {
        current += char;
        const isKey = isKeyQuote(i + 1);
        parts.push({ text: current, color: isKey ? colors.key : colors.quotedValue });
        current = '';
        inQuotes = false;
      } else {
        if (current.trim()) {
          const trimmed = current.trim();
          if (trimmed === 'AND' || trimmed === 'OR') {
            parts.push({ text: current.replace(trimmed, ''), color: 'inherit' });
            parts.push({ text: trimmed, color: colors.operator });
          } else {
            parts.push({ text: current, color: 'inherit' });
          }
          current = '';
        }
        current = char;
        inQuotes = true;
      }
    } else if (char === '(' && !inQuotes) {
      if (current.trim()) {
        const trimmed = current.trim();
        if (trimmed === 'AND' || trimmed === 'OR') {
          parts.push({ text: current.replace(trimmed, ''), color: 'inherit' });
          parts.push({ text: trimmed, color: colors.operator });
        } else {
          parts.push({ text: current, color: 'inherit' });
        }
        current = '';
      }
      parts.push({ text: char, color: getParenColor(parenDepth) });
      parenDepth++;
    } else if (char === ')' && !inQuotes) {
      if (current.trim()) {
        processNonParenText(current, parts, colors);
        current = '';
      }
      parenDepth--;
      parts.push({ text: char, color: getParenColor(parenDepth) });
    } else if ((char === ':' || char === '=') && !inQuotes) {
      if (current.trim()) {
        parts.push({ text: current, color: colors.key });
        current = '';
      }
      parts.push({ text: char, color: char === ':' ? colors.colon : colors.equals });
    } else if (/\s/.test(char) && !inQuotes) {
      if (current.trim()) {
        processNonParenText(current, parts, colors);
        current = '';
      }
      parts.push({ text: char, color: 'inherit' });
    } else {
      current += char;
    }
  }

  if (current) {
    if (inQuotes) {
      parts.push({ text: current, color: colors.quotedValue });
    } else {
      processNonParenText(current, parts, colors);
    }
  }

  return parts;
};

const processNonParenText = (
  text: string,
  parts: { text: string; color: string }[],
  colors: any,
) => {
  const trimmed = text.trim();
  if (trimmed === 'AND' || trimmed === 'OR') {
    parts.push({ text: text.replace(trimmed, ''), color: 'inherit' });
    parts.push({ text: trimmed, color: colors.operator });
  } else if (trimmed && !trimmed.includes(':')) {
    parts.push({ text: text, color: colors.value });
  } else {
    parts.push({ text: text, color: 'inherit' });
  }
};

interface FilterNode {
  id: string;
  type: 'condition' | 'group';
  key?: string;
  fieldKey?: LeadFieldKey;
  value?: string;
  operator?: OperatorType;
  children?: FilterNode[];
}

type GenerateNodeId = () => string;

interface MetadataFilterComponentProps {
  value: MetadataFilter | null;
  onChange: (filter: MetadataFilter | null) => void;
  mode?: FilterMode;
  onModeChange?: (mode: FilterMode) => void;
  initialQuery?: string;
  onQueryChange?: (query: string) => void;
  onDialogClose?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  disabled?: boolean;
  disabledMessage?: string;
}

interface SyntaxHighlightedInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

const SyntaxHighlightedInput: FC<SyntaxHighlightedInputProps> = ({
  value,
  onChange,
  placeholder,
  error,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  const handleInput = () => {
    const text = editorRef.current?.textContent || '';
    setLocalValue(text);
    onChange(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const insertCharacter = (character: string) => {
      e.preventDefault();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const tabNode = document.createTextNode(character);
        range.insertNode(tabNode);
        range.setStartAfter(tabNode);
        range.setEndAfter(tabNode);
        selection.removeAllRanges();
        selection.addRange(range);
        handleInput();
      }
    };

    if (e.key === 'Tab') {
      insertCharacter('\t');
    } else if (e.key === 'Enter') {
      insertCharacter('\n');
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
      handleInput();
    }
  };

  useEffect(() => {
    if (editorRef.current && !editorRef.current.textContent) {
      editorRef.current.textContent = value;
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !isFocused) {
      const currentText = editorRef.current.textContent || '';
      if (currentText !== value) {
        editorRef.current.textContent = value;
        setLocalValue(value);
      }
    }
  }, [value, isFocused]);

  const displayValue = isFocused ? localValue : value;

  const renderHighlightedContent = () => {
    if (!displayValue && placeholder) {
      return (
        <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          {placeholder}
        </Box>
      );
    }

    const parts = highlightQuerySyntax(displayValue);
    return parts.map((part, index) => (
      <Box
        key={index}
        component="span"
        sx={{
          color: part.color,
          fontWeight: 500,
        }}
      >
        {part.text}
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: 100,
        border: (theme) =>
          `1px solid ${
            error
              ? theme.palette.error.main
              : isFocused
              ? theme.palette.primary.main
              : theme.palette.divider
          }`,
        borderRadius: 1,
        backgroundColor: 'background.paper',
        transition: 'border-color 0.2s',
        '&:hover': {
          borderColor: error ? 'error.main' : 'primary.main',
        },
      }}
    >
      <Box
        ref={editorRef}
        contentEditable
        spellCheck={false}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        suppressContentEditableWarning
        sx={{
          position: 'relative',
          zIndex: 2,
          p: 1.5,
          minHeight: 100,
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.8,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          outline: 'none',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
          caretColor: (theme) => theme.palette.text.primary,
          tabSize: 4,
          '&:empty:before': {
            content: placeholder ? `"${placeholder}"` : '""',
            color: 'transparent',
            WebkitTextFillColor: (theme) => theme.palette.text.disabled,
            fontStyle: 'italic',
          },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          p: 1.5,
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.8,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          pointerEvents: 'none',
          zIndex: 1,
          tabSize: 4,
        }}
      >
        {renderHighlightedContent()}
      </Box>
    </Box>
  );
};

const convertMetadataFilterToNode = (
  filter: MetadataFilter | null,
  generateId: GenerateNodeId,
): FilterNode | null => {
  if (!filter) return null;

  if ((filter.key || filter.fieldKey) && filter.value) {
    return {
      id: generateId(),
      type: 'condition',
      key: filter.key,
      fieldKey: filter.fieldKey,
      value: filter.value,
    };
  }

  if (filter.and) {
    return {
      id: generateId(),
      type: 'group',
      operator: 'AND',
      children: filter.and
        .map((f) => convertMetadataFilterToNode(f, generateId))
        .filter((n): n is FilterNode => n !== null),
    };
  }

  if (filter.or) {
    return {
      id: generateId(),
      type: 'group',
      operator: 'OR',
      children: filter.or
        .map((f) => convertMetadataFilterToNode(f, generateId))
        .filter((n): n is FilterNode => n !== null),
    };
  }

  return null;
};

const convertNodeToMetadataFilter = (node: FilterNode | null): MetadataFilter | null => {
  if (!node) return null;

  if (node.type === 'condition') {
    if (!node.value?.trim()) return null;
    if (!node.key?.trim() && !node.fieldKey) return null;

    const filter: MetadataFilter = {
      value: node.value,
    };

    if (node.fieldKey) {
      filter.fieldKey = node.fieldKey;
    } else if (node.key) {
      filter.key = node.key;
    }

    return filter;
  }

  if (node.type === 'group' && node.children && node.children.length > 0) {
    const validChildren = node.children
      .map((child) => convertNodeToMetadataFilter(child))
      .filter((f): f is MetadataFilter => f !== null);

    if (validChildren.length === 0) return null;
    if (validChildren.length === 1) return validChildren[0];

    if (node.operator === 'AND') {
      return { and: validChildren };
    } else {
      return { or: validChildren };
    }
  }

  return null;
};

// Convert MetadataFilter to text query syntax
const filterToText = (filter: MetadataFilter | null, isRoot = true): string => {
  if (!filter) return '';

  if ((filter.key || filter.fieldKey) && filter.value) {
    let fieldName: string;
    let useEqualsFormat = false;

    if (filter.fieldKey) {
      // Use fieldKey display name and equals format
      fieldName = getFieldKeyLabel(filter.fieldKey);
      useEqualsFormat = true;
    } else if (filter.key) {
      fieldName = filter.key;
      // Wrap key in quotes if it contains spaces, special characters, or quotes
      const keyNeedsQuotes = /[\s():"]/.test(fieldName);
      if (keyNeedsQuotes) {
        fieldName = fieldName.replace(/"/g, '\\"');
        fieldName = `"${fieldName}"`;
      }
    } else {
      return '';
    }

    // Always quote the value for fieldKey format
    let value = filter.value;
    if (useEqualsFormat) {
      value = value.replace(/"/g, '\\"');
      value = `"${value}"`;
      return `${fieldName}=${value}`;
    } else {
      // Wrap value in quotes if it contains spaces, special characters, or quotes
      const valueNeedsQuotes = /[\s()"]/.test(value);
      if (valueNeedsQuotes) {
        value = value.replace(/"/g, '\\"');
        value = `"${value}"`;
      }
      return `${fieldName}:${value}`;
    }
  }

  if (filter.and && filter.and.length > 0) {
    const parts = filter.and.map((f) => filterToText(f, false));
    const result = parts.join(' AND ');
    // Always wrap non-root AND groups in parentheses
    return isRoot ? result : `(${result})`;
  }

  if (filter.or && filter.or.length > 0) {
    const parts = filter.or.map((f) => filterToText(f, false));
    const result = parts.join(' OR ');
    // Always wrap non-root OR groups in parentheses
    return isRoot ? result : `(${result})`;
  }

  return '';
};

// Parse text query syntax to MetadataFilter
export const parseTextToFilter = (
  text: string,
): { filter: MetadataFilter | null; error: string | null } => {
  if (!text.trim()) return { filter: null, error: null };

  try {
    // Tokenize the input
    const tokens: string[] = [];
    let current = '';
    let inQuotes = false;
    let parenDepth = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const prevChar = i > 0 ? text[i - 1] : '';

      // Check for escaped quote (\" inside quotes should be treated as literal)
      if (char === '"' && prevChar !== '\\') {
        inQuotes = !inQuotes;
        current += char;
      } else if (char === '\\' && i + 1 < text.length && text[i + 1] === '"') {
        // Skip backslash, but keep the quote that follows (will be added in next iteration)
        continue;
      } else if (char === '(' && !inQuotes) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push('(');
        parenDepth++;
      } else if (char === ')' && !inQuotes) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push(')');
        parenDepth--;
      } else if (/\s/.test(char) && !inQuotes) {
        // Treat all whitespace (spaces, newlines, tabs) as token separators
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current.trim()) {
      tokens.push(current.trim());
    }

    if (parenDepth !== 0) {
      return { filter: null, error: 'Mismatched parentheses' };
    }

    // Parse tokens into filter structure
    const maxDepth = 2; // Allow 3 levels total (0, 1, 2) - same as visual builder
    let errorMessage: string | null = null;

    const parseTokens = (tokens: string[], depth = 0): MetadataFilter | null => {
      if (tokens.length === 0) return null;

      // Check depth limit
      if (depth > maxDepth) {
        errorMessage = 'Maximum nesting depth exceeded (3 levels allowed)';
        return null;
      }

      // Find ALL top-level OR operators (lowest precedence)
      let parenDepth = 0;
      const orIndices: number[] = [];

      for (let i = 0; i < tokens.length; i++) {
        if (tokens[i] === '(') parenDepth++;
        else if (tokens[i] === ')') parenDepth--;
        else if (parenDepth === 0 && tokens[i] === 'OR') {
          orIndices.push(i);
        }
      }

      // If we found OR operators, split by all of them
      if (orIndices.length > 0) {
        const parts: MetadataFilter[] = [];
        let start = 0;

        for (const idx of orIndices) {
          const part = parseTokens(tokens.slice(start, idx), depth);
          if (part) {
            parts.push(part);
          } else if (errorMessage) {
            // Propagate error from failed parse
            return null;
          }
          start = idx + 1;
        }

        const lastPart = parseTokens(tokens.slice(start), depth);
        if (lastPart) {
          parts.push(lastPart);
        } else if (errorMessage) {
          // Propagate error from failed parse
          return null;
        }

        return parts.length > 0 ? { or: parts } : null;
      }

      // Find ALL top-level AND operators (higher precedence)
      parenDepth = 0;
      const andIndices: number[] = [];

      for (let i = 0; i < tokens.length; i++) {
        if (tokens[i] === '(') parenDepth++;
        else if (tokens[i] === ')') parenDepth--;
        else if (parenDepth === 0 && tokens[i] === 'AND') {
          andIndices.push(i);
        }
      }

      // If we found AND operators, split by all of them
      if (andIndices.length > 0) {
        const parts: MetadataFilter[] = [];
        let start = 0;

        for (const idx of andIndices) {
          const part = parseTokens(tokens.slice(start, idx), depth);
          if (part) {
            parts.push(part);
          } else if (errorMessage) {
            // Propagate error from failed parse
            return null;
          }
          start = idx + 1;
        }

        const lastPart = parseTokens(tokens.slice(start), depth);
        if (lastPart) {
          parts.push(lastPart);
        } else if (errorMessage) {
          // Propagate error from failed parse
          return null;
        }

        return parts.length > 0 ? { and: parts } : null;
      }

      // Handle parentheses (before implicit AND check)
      // Handle parentheses (before implicit AND check)
      if (tokens[0] === '(' && tokens[tokens.length - 1] === ')') {
        // Verify the opening paren at index 0 actually closes at the last position
        let depth = 0;
        let closesAtEnd = false;
        for (let i = 0; i < tokens.length; i++) {
          if (tokens[i] === '(') depth++;
          else if (tokens[i] === ')') {
            depth--;
            if (depth === 0) {
              closesAtEnd = i === tokens.length - 1;
              break;
            }
          }
        }
        if (closesAtEnd) {
          return parseTokens(tokens.slice(1, -1), depth + 1);
        }
        return parseTokens(tokens.slice(1, -1), depth + 1);
      }

      // If no explicit operators but multiple tokens, treat as implicit AND
      if (tokens.length > 1) {
        const parts: MetadataFilter[] = [];

        for (const token of tokens) {
          const part = parseTokens([token], depth);
          if (part) {
            parts.push(part);
          } else if (errorMessage) {
            // Propagate error from failed parse
            return null;
          }
        }

        if (parts.length === 0) return null;
        if (parts.length === 1) return parts[0];
        return { and: parts };
      }

      // Parse key:value or fieldName="value" pair
      if (tokens.length === 1) {
        const token = tokens[0];
        const colonIndex = token.indexOf(':');
        const equalsIndex = token.indexOf('=');

        // Check for fieldName="value" format (equals has higher priority)
        if (equalsIndex > 0 && (colonIndex === -1 || equalsIndex < colonIndex)) {
          let fieldName = token.substring(0, equalsIndex);
          let value = token.substring(equalsIndex + 1);

          // Remove quotes from fieldName if present
          if (fieldName.startsWith('"') && fieldName.endsWith('"')) {
            fieldName = fieldName.slice(1, -1);
            fieldName = fieldName.replace(/\\"/g, '"');
          }

          // Validate fieldName
          if (!fieldName.trim()) {
            errorMessage = 'Field name cannot be empty';
            return null;
          }

          // Parse fieldKey from label
          const fieldKey = parseFieldKeyFromLabel(fieldName);
          if (!fieldKey) {
            errorMessage = `Unknown field name "${fieldName}". Valid fields: ${FIELD_KEY_OPTIONS.map(
              (f) => f.label,
            ).join(', ')}`;
            return null;
          }

          // Remove quotes from value if present and unescape any escaped quotes inside
          if (value.startsWith('"') && value.endsWith('"')) {
            value = value.slice(1, -1);
            value = value.replace(/\\"/g, '"');
          }

          if (!value.trim()) {
            errorMessage = `Value for field "${fieldName}" cannot be empty`;
            return null;
          }

          return { fieldKey, value };
        }
        // Check for key:value format
        else if (colonIndex > 0) {
          let key = token.substring(0, colonIndex);
          let value = token.substring(colonIndex + 1);

          // Remove quotes from key if present and unescape any escaped quotes inside
          if (key.startsWith('"') && key.endsWith('"')) {
            key = key.slice(1, -1);
            key = key.replace(/\\"/g, '"');
          }

          // Validate key and value
          if (!key.trim()) {
            errorMessage = 'Key cannot be empty';
            return null;
          }

          if (/\s/.test(key)) {
            errorMessage = `Key "${key}" cannot contain spaces`;
            return null;
          }

          if (!value.trim()) {
            errorMessage = `Value for key "${key}" cannot be empty`;
            return null;
          }

          // Remove quotes from value if present and unescape any escaped quotes inside
          if (value.startsWith('"') && value.endsWith('"')) {
            value = value.slice(1, -1);
            value = value.replace(/\\"/g, '"');
          }

          return { key, value };
        } else if (colonIndex === 0 || equalsIndex === 0) {
          errorMessage = 'Key/field name cannot be empty';
          return null;
        } else {
          // Token without separator is invalid
          errorMessage = `Invalid token "${token}". Expected format: key:value or fieldName="value"`;
          return null;
        }
      }

      return null;
    };

    const result = parseTokens(tokens);

    // Check if an error was set
    if (!result && errorMessage) {
      return { filter: null, error: errorMessage };
    }

    return { filter: result, error: result ? null : 'Invalid query syntax' };
  } catch (error) {
    return {
      filter: null,
      error: error instanceof Error ? error.message : 'Invalid query syntax',
    };
  }
};

interface FilterConditionProps {
  node: FilterNode;
  onUpdate: (node: FilterNode) => void;
  onRemove: () => void;
}

const MetadataTextField = (props: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error: boolean;
}) => (
  <TextField
    label={props.label}
    value={props.value}
    onChange={props.onChange}
    size="small"
    error={props.error}
    sx={{
      flex: 1,
      maxWidth: 200,
      '& .MuiInputBase-input': {
        fontSize: '0.875rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    }}
  />
);

const FilterCondition: FC<FilterConditionProps> = ({ node, onUpdate, onRemove }) => {
  const fieldEmpty = !node.key && !node.fieldKey;
  const valueEmpty = !node.value || node.value.trim() === '';

  const currentFieldValue = node.fieldKey ? getFieldKeyLabel(node.fieldKey) : node.key || '';
  const fieldLabel = node.fieldKey ? 'Field' : 'Metadata';
  const matchesKnownField = currentFieldValue && parseFieldKeyFromLabel(currentFieldValue);
  const showToggle = !!matchesKnownField;

  const handleFieldChange = (_event: any, newValue: string | null) => {
    if (!newValue) {
      onUpdate({ ...node, key: undefined, fieldKey: undefined });
      return;
    }

    // Check if it's a known field key
    const fieldKey = parseFieldKeyFromLabel(newValue);
    if (fieldKey) {
      onUpdate({ ...node, key: undefined, fieldKey });
    } else {
      // Custom key - strip spaces
      onUpdate({ ...node, fieldKey: undefined, key: newValue.replace(/\s/g, '') });
    }
  };

  const handleToggleFieldType = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentFieldValue) return;

    if (node.fieldKey) {
      // Switch from built-in field to custom metadata
      onUpdate({ ...node, fieldKey: undefined, key: currentFieldValue });
    } else if (node.key) {
      // Switch from custom metadata to built-in field
      const fieldKey = parseFieldKeyFromLabel(currentFieldValue);
      if (fieldKey) {
        onUpdate({ ...node, key: undefined, fieldKey });
      }
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        '& .delete-button': {
          opacity: 0,
          transition: 'opacity 0.2s',
        },
        '&:hover .delete-button': {
          opacity: 1,
        },
      }}
    >
      <Autocomplete
        freeSolo
        options={FIELD_KEY_OPTIONS.map((f) => f.label)}
        value={currentFieldValue}
        onChange={handleFieldChange}
        onInputChange={(_event, newInputValue, reason) => {
          // Only fires for typing, not selection
          if (reason === 'input') {
            handleFieldChange(null, newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={fieldLabel}
            size="small"
            error={fieldEmpty}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {showToggle && (
                      <Tooltip title={`Switch to ${node.fieldKey ? 'Metadata' : 'Field'}`}>
                        <IconButton size="small" onClick={handleToggleFieldType} sx={{ mr: -0.5 }}>
                          <SwapHoriz fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        sx={{
          flex: 1,
          maxWidth: 200,
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
        }}
      />
      <MetadataTextField
        label="Value"
        value={node.value || ''}
        onChange={(e) => onUpdate({ ...node, value: e.target.value })}
        error={valueEmpty}
      />
      <TooltipIconButton
        tooltip="Remove condition"
        onClick={onRemove}
        iconButtonProps={{
          color: 'error',
          size: 'small',
          className: 'delete-button',
        }}
      >
        <Delete />
      </TooltipIconButton>
    </Stack>
  );
};

interface FilterGroupProps {
  node: FilterNode;
  onUpdate: (node: FilterNode) => void;
  onRemove: () => void;
  depth: number;
  generateId: GenerateNodeId;
}

const DividerLine: FC<
  { horizontal: true; vertical?: never } | { vertical: true; horizontal?: never }
> = ({ horizontal, vertical }) => {
  if (horizontal) {
    return (
      <Box
        sx={{
          width: '16px',
          height: '1px',
          backgroundColor: 'divider',
          // mt: 1.5,
        }}
      />
    );
  } else if (vertical) {
    return (
      <Box
        sx={{
          width: '1px',
          flex: 1,
          backgroundColor: 'divider',
          minHeight: '20px',
        }}
      />
    );
  }
  return null;
};

const FilterGroup: FC<FilterGroupProps> = ({ node, onUpdate, onRemove, depth, generateId }) => {
  const addCondition = () => {
    const newChild: FilterNode = {
      id: generateId(),
      type: 'condition',
      key: '',
      value: '',
    };
    onUpdate({
      ...node,
      children: [...(node.children || []), newChild],
    });
  };

  const addGroup = () => {
    const newChild: FilterNode = {
      id: generateId(),
      type: 'group',
      operator: 'AND',
      children: [],
    };
    onUpdate({
      ...node,
      children: [...(node.children || []), newChild],
    });
  };

  const updateChild = (index: number, updatedChild: FilterNode) => {
    const newChildren = [...(node.children || [])];
    newChildren[index] = updatedChild;
    onUpdate({ ...node, children: newChildren });
  };

  const removeChild = (index: number) => {
    const newChildren = node.children?.filter((_, i) => i !== index) || [];
    onUpdate({ ...node, children: newChildren });
  };

  const changeOperator = (operator: OperatorType) => {
    onUpdate({ ...node, operator });
  };

  const showOperator = node.children && node.children.length >= 2;
  const maxDepth = 2; // Allow 3 levels total (0, 1, 2)
  const maxConditions = 10;
  const conditionCount = node.children?.filter((child) => child.type === 'condition').length || 0;
  const canAddGroup = depth < maxDepth;
  const canAddCondition = conditionCount < maxConditions;

  const getBackgroundColor = (depth: number) => {
    const colors = ['background.paper', 'action.hover', 'action.selected'];
    return colors[depth % colors.length];
  };

  return (
    <Box
      sx={{
        backgroundColor: getBackgroundColor(depth),
        p: 2,
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Stack direction="row" spacing={0}>
        {/* Bracket-style operator on the left - only show if 2+ items */}
        {showOperator && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mr: 2,
              position: 'relative',
            }}
          >
            <DividerLine horizontal />
            <DividerLine vertical />

            {/* Operator dropdown */}
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={node.operator || 'AND'}
                onChange={(e) => changeOperator(e.target.value as OperatorType)}
                sx={{
                  fontSize: '0.75rem',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 1,
                  },
                }}
              >
                <MenuItem value="AND" sx={{ fontSize: '0.75rem' }}>
                  AND
                </MenuItem>
                <MenuItem value="OR" sx={{ fontSize: '0.75rem' }}>
                  OR
                </MenuItem>
              </Select>
            </FormControl>

            <DividerLine vertical />
            <DividerLine horizontal />
          </Box>
        )}

        {/* Content area - only conditions/groups */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            pr: depth > 0 ? 5 : 0,
            '& > .delete-button': {
              opacity: 0,
              transition: 'opacity 0.2s',
            },
            '&:hover > .delete-button': {
              opacity: 1,
            },
          }}
        >
          {depth > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                zIndex: 1,
              }}
              className="delete-button"
            >
              <TooltipIconButton
                tooltip="Remove group"
                onClick={onRemove}
                iconButtonProps={{
                  color: 'error',
                  size: 'small',
                }}
                tooltipProps={{
                  placement: 'left',
                }}
              >
                <Delete />
              </TooltipIconButton>
            </Box>
          )}

          {(!node.children || node.children.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', my: 1 }}>
              No conditions in this group
            </Typography>
          )}

          <Stack spacing={1.5}>
            {node.children?.map((child, index) => (
              <Box key={child.id}>
                {child.type === 'condition' ? (
                  <FilterCondition
                    node={child}
                    onUpdate={(updated) => updateChild(index, updated)}
                    onRemove={() => removeChild(index)}
                  />
                ) : (
                  <FilterGroup
                    node={child}
                    onUpdate={(updated) => updateChild(index, updated)}
                    onRemove={() => removeChild(index)}
                    depth={depth + 1}
                    generateId={generateId}
                  />
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>

      {/* Action buttons at bottom - outside the bracket */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 2, ml: showOperator ? 10 : 0 }}
      >
        <Button
          startIcon={<Add />}
          onClick={addCondition}
          size="small"
          variant="outlined"
          disabled={!canAddCondition}
          title={!canAddCondition ? 'Maximum 10 conditions per group' : ''}
        >
          Add Condition
        </Button>

        <Button
          startIcon={<Add />}
          onClick={addGroup}
          size="small"
          variant="outlined"
          disabled={!canAddGroup}
          title={!canAddGroup ? 'Maximum nesting depth reached (3 levels)' : ''}
        >
          Add Group
        </Button>
      </Stack>
    </Box>
  );
};

export const MetadataFilters: FC<MetadataFilterComponentProps> = ({
  value,
  onChange,
  mode: controlledMode,
  onModeChange,
  initialQuery,
  onQueryChange,
  onValidationChange,
}) => {
  const [internalMode, setInternalMode] = useState<FilterMode>('visual');
  const mode = controlledMode ?? internalMode;
  const setMode = onModeChange ?? setInternalMode;

  const [textQuery, setTextQuery] = useState<string>(() => initialQuery || filterToText(value));
  const [parseError, setParseError] = useState<string | null>(null);

  // Create ID generator scoped to this component instance
  const nodeIdCounter = useRef(1);
  const generateId: GenerateNodeId = () => {
    return `filter-node-${nodeIdCounter.current++}`;
  };

  const [rootNode, setRootNode] = useState<FilterNode>(() => {
    const converted = convertMetadataFilterToNode(value, generateId);
    if (converted?.type === 'group') {
      return converted;
    }
    // Create a default AND group
    return {
      id: 'root',
      type: 'group',
      operator: 'AND',
      children: converted ? [converted] : [],
    };
  });

  // Track the last text representation to detect actual changes
  const lastSentTextQuery = useRef<string>(filterToText(value));

  // Notify parent of validation state changes
  useEffect(() => {
    onValidationChange?.(parseError === null);
  }, [parseError, onValidationChange]);

  // Sync internal state when value changes externally (e.g., from clear button)
  useEffect(() => {
    const newTextQuery = filterToText(value);

    // Only sync if the text representation actually changed
    // This prevents re-syncing when the object reference changes but content is the same
    if (newTextQuery === lastSentTextQuery.current) {
      return;
    }

    // Update text query
    setTextQuery(newTextQuery);
    lastSentTextQuery.current = newTextQuery;

    // Update visual representation
    const converted = convertMetadataFilterToNode(value, generateId);
    if (converted?.type === 'group') {
      setRootNode(converted);
    } else if (converted) {
      setRootNode({
        id: 'root',
        type: 'group',
        operator: 'AND',
        children: [converted],
      });
    } else {
      // Value is null, reset to empty group
      setRootNode({
        id: 'root',
        type: 'group',
        operator: 'AND',
        children: [],
      });
    }
    setParseError(null);
  }, [value]);

  const handleVisualUpdate = (node: FilterNode) => {
    setRootNode(node);
    const filter = convertNodeToMetadataFilter(node);
    onChange(filter);
    // Sync text query
    const textRepresentation = filterToText(filter);
    setTextQuery(textRepresentation);
    lastSentTextQuery.current = textRepresentation;
    onQueryChange?.(textRepresentation); // Persist query when editing in visual mode
  };

  const handleTextUpdate = (text: string) => {
    setTextQuery(text);

    // Don't validate if empty
    if (!text.trim()) {
      setParseError(null);
      lastSentTextQuery.current = '';
      onChange(null);
      return;
    }

    const { filter, error } = parseTextToFilter(text);

    if (error || !filter) {
      // Show error but keep the text as-is - don't notify parent or clear filter
      setParseError(error || 'Invalid query syntax');
      return;
    }

    setParseError(null);
    lastSentTextQuery.current = text;
    onChange(filter);
    onQueryChange?.(text); // Persist query on successful parse

    // Sync visual representation
    const converted = convertMetadataFilterToNode(filter, generateId);
    if (converted?.type === 'group') {
      setRootNode(converted);
    } else if (converted) {
      setRootNode({
        id: 'root',
        type: 'group',
        operator: 'AND',
        children: [converted],
      });
    }
  };

  const handleModeChange = (newMode: string) => {
    const newModeTyped = newMode as FilterMode;

    // Prevent mode change if already in that mode
    if (newModeTyped === mode) {
      return;
    }

    // When switching to text mode, sync the text query
    if (newModeTyped === 'text') {
      const filter = convertNodeToMetadataFilter(rootNode);
      const newTextQuery = filterToText(filter);
      setTextQuery(newTextQuery);
      setParseError(null);
      setMode(newModeTyped);
      return;
    }

    // When switching to visual mode, validate the text query first
    if (newModeTyped === 'visual') {
      if (!textQuery.trim()) {
        // Allow switching if empty
        setParseError(null);
        setMode(newModeTyped);
        return;
      }

      const { filter, error } = parseTextToFilter(textQuery);
      if (error || !filter) {
        // Keep current text and show error - do NOT switch modes
        setParseError(`Cannot switch to visual mode: ${error || 'Invalid query syntax'}`);
        // Ensure mode stays as 'text' even if parent tries to change it
        if (mode !== 'text') {
          setMode('text');
        }
        return;
      }

      // Valid query - sync to visual mode
      const converted = convertMetadataFilterToNode(filter, generateId);
      if (converted?.type === 'group') {
        setRootNode(converted);
      } else if (converted) {
        setRootNode({
          id: 'root',
          type: 'group',
          operator: 'AND',
          children: [converted],
        });
      }

      setParseError(null);
      setMode(newModeTyped);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PurpleToggle
        value={mode}
        onChange={handleModeChange}
        sx={{ marginBottom: 1 }}
        options={[
          { value: 'visual', label: 'Visual' },
          { value: 'text', label: 'Text' },
        ]}
        size="small"
      />

      {mode === 'visual' ? (
        <FilterGroup
          node={rootNode}
          onUpdate={handleVisualUpdate}
          onRemove={() => {}}
          depth={0}
          generateId={generateId}
        />
      ) : (
        <Box>
          <SyntaxHighlightedInput
            value={textQuery}
            onChange={handleTextUpdate}
            placeholder='e.g., state="CA" AND source="web" OR city="Austin"'
            error={!!parseError}
          />
          <Typography
            variant="caption"
            color={parseError ? 'error' : 'text.secondary'}
            sx={{ mt: 1, display: 'block' }}
          >
            {parseError ||
              'Syntax: fieldName="value" for built-in fields (region, source) or key:value for custom metadata. Use AND, OR for logic. Parentheses for grouping. Keys cannot contain spaces.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const MetadataFiltersDialog: FC<MetadataFilterComponentProps> = ({
  value,
  onChange,
  initialQuery,
  onQueryChange,
  onDialogClose,
  disabled,
  disabledMessage,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<FilterMode>('visual');
  const [isValid, setIsValid] = useState(true);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (!isValid) {
      return;
    }
    setOpen(false);
    onDialogClose?.();
  };

  const handleClear = () => {
    onChange(null);
    onQueryChange?.('');
  };

  const hasFilters = value !== null;

  return (
    <>
      <Badge
        color={!disabled ? 'primary' : 'warning'}
        variant="dot"
        invisible={!hasFilters}
        sx={{
          '& .MuiBadge-badge': {
            right: 8,
            top: 8,
          },
        }}
      >
        <TooltipIconButton
          tooltip={disabled && disabledMessage ? disabledMessage : 'Filter by fields or metadata'}
          onClick={handleOpen}
          iconButtonProps={{ disabled }}
        >
          <FilterList />
        </TooltipIconButton>
      </Badge>

      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (!isValid) {
              return;
            }
          }
          handleClose();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <MetadataFilters
              value={value}
              onChange={onChange}
              mode={mode}
              onModeChange={setMode}
              initialQuery={initialQuery}
              onQueryChange={onQueryChange}
              onValidationChange={setIsValid}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} color="error">
            Clear Filters
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose} variant="contained" disabled={!isValid}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
